import { Field, FieldArray, useField } from "formik";
import React, { useEffect, useRef, useState } from "react";
import { FlatCubeIcon } from "components/Icons/FlatCubeIcon";
import { packClasses } from "Utils";
import BaseElement from "./BaseElement";
import TokenButton from "components/Buttons/TokenButton";

export default function AutocompleteInput(props) {
    const [searching, setSearching] = useState(false);
    const [hasResults, setHasResults] = useState(false);
    const [menuOpen, setMenuOpen] = useState(false);
    const [listItems, setListItems] = useState([]);
    const [selectedIndex, setSelectedIndex] = useState(0);
    const [didInit, setDidInit] = useState(false);

    const [field, meta] = useField(props);

    const inputRef = useRef();

    const onInit = () => {
        if (props.preSelect && !didInit) {
            props.preSelect().then((data) => {
                setSelectedIndex(0);
                setListItems(data);
                setHasResults(true);
            });
            setDidInit(true);
        }
    };

    const doSearch = (term) => {
        if (searching) {
            return;
        }
        setSearching(true);
        if (props.onRequestSearch) {
            props
                .onRequestSearch(term)
                .then((data) => {
                    setSearching(false);
                    setSelectedIndex(0);
                    setListItems(data);
                    setMenuOpen(true);
                    setHasResults(true);
                })
                .catch((e) => {
                    setSearching(false);
                    setSelectedIndex(0);
                    setListItems([]);
                    setMenuOpen(false);
                    setHasResults(false);
                    console.info("Search failed", e);
                });
        }
    };

    const menuClickHandler = (arrayHelpers, item, e) => {
        const existing =
            field.value?.length &&
            field.value.find((i) => i.value === item.value);
        if (!existing) {
            arrayHelpers.push(item);
        }

        setMenuOpen(false);
    };

    const keyDownHandler = (arrayHelpers, e) => {
        if (props.disabled) {
            return;
        }
        if (e.key === "ArrowDown") {
            e.preventDefault();
            if (selectedIndex < listItems.length - 1) {
                setSelectedIndex(selectedIndex + 1);
            }
        }
        if (e.key === "ArrowUp") {
            e.preventDefault();
            if (selectedIndex > 0) {
                setSelectedIndex(selectedIndex - 1);
            }
        }
        if (e.key === "Enter") {
            e.preventDefault();
            if (listItems.length === 0) {
                return;
            }
            const existing =
                field.value?.length &&
                field.value.find(
                    (item) => item.value === listItems[selectedIndex].value
                );
            if (!existing) {
                arrayHelpers.push(listItems[selectedIndex]);
            }
            setMenuOpen(false);
            return;
        }
        if (e.key === "Escape") {
            e.preventDefault();
            setMenuOpen(false);
        }
    };

    let timeout;
    const handleSearch = (term) => {
        if (timeout) {
            clearTimeout(timeout);
        }
        timeout = setTimeout(() => {
            if (!term || term.length < 2) {
                return;
            }
            setMenuOpen(false);

            console.log("Search for ", term);
            doSearch(term);
        }, 500);

        return () => {
            clearTimeout(timeout);
        };
    };

    onInit();

    const input = (
        <div>
            <FieldArray
                name={field.name}
                render={(arrayHelpers) => (
                    <div>
                        <div className="relative mt-1 mb-2 rounded-md shadow-sm">
                            <input
                                type="text"
                                name="auto_complete"
                                autoComplete="off"
                                disabled={props.disabled}
                                ref={inputRef}
                                className="block w-full rounded-md border-gray-300 pr-10 focus:border-green-500 focus:ring-green-500 sm:text-sm"
                                onChange={(e) => {
                                    handleSearch(e.target.value);
                                }}
                                onFocus={(e) => {
                                    if (hasResults) {
                                        setMenuOpen(true);
                                    }
                                }}
                                onBlur={(e) => {
                                    // slight timeout to allow for the click handler on the menu
                                    setTimeout(() => setMenuOpen(false), 200);
                                }}
                                onKeyDown={keyDownHandler.bind(
                                    this,
                                    arrayHelpers
                                )}
                            />
                            <div
                                className={"pointer-events-none absolute inset-y-0 right-0 flex items-center pr-3".concat(
                                    searching ? "" : " hidden"
                                )}
                            >
                                <FlatCubeIcon
                                    className="h-5 w-5 animate-spin text-gray-900"
                                    aria-hidden="true"
                                />
                            </div>
                        </div>
                        <div
                            className={packClasses(
                                menuOpen ? "" : "hidden",
                                "relative"
                            )}
                        >
                            <div className="max-w-full">
                                <ul className="absolute top-0 left-0 z-50 max-h-72 min-w-full overflow-scroll bg-white shadow">
                                    {listItems.map((item, idx) => (
                                        <li
                                            className={packClasses(
                                                selectedIndex === idx
                                                    ? "bg-gray-100"
                                                    : "",
                                                "cursor-pointer px-6 py-2 hover:bg-gray-100"
                                            )}
                                            onClick={menuClickHandler.bind(
                                                this,
                                                arrayHelpers,
                                                item
                                            )}
                                            key={"autocomplete".concat(idx)}
                                        >
                                            <span className="text-sm font-light text-gray-700">
                                                {item.label}
                                            </span>
                                        </li>
                                    ))}
                                </ul>
                            </div>
                        </div>

                        {field.value
                            ? field.value.map((val, idx) => (
                                  <div
                                      key={idx}
                                      onClick={() => {
                                          if (props.disabled) {
                                              return;
                                          }

                                          return arrayHelpers.remove(idx);
                                      }}
                                      className="mr-1 inline-flex"
                                  >
                                      <Field
                                          name={field.name + "" + idx}
                                          value={val.value}
                                          className="hidden"
                                      />
                                      <TokenButton label={val.label} />
                                  </div>
                              ))
                            : null}
                    </div>
                )}
            />
        </div>
    );

    const label = (
        <label
            htmlFor={props.id}
            className="block text-sm font-medium text-gray-700"
        >
            {props.label}
        </label>
    );

    const helperText = props.helpertext ? (
        <p className="mt-2 text-sm text-gray-500">{props.helpertext}</p>
    ) : (
        ""
    );

    const errorText =
        props.isError || (meta.touched && meta.error) ? (
            <p className="mt-2 text-sm text-red-600">
                {props.errorText}
                {meta.error}
            </p>
        ) : (
            ""
        );

    useEffect(() => {
        if (!props.onChange) {
            return;
        }
        props.onChange(field.value);
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [field.value]);

    return (
        <BaseElement
            {...props}
            input={input}
            label={label}
            helpertext={helperText}
            errortext={errorText}
        />
    );
}
